import { vaultFilesApi } from "@api";
import { queriesVaultFiles } from "@query";
import { useWSSnackbar } from "@wingspanhq/fe-component-library";

import { downloadFileFromBuffer } from "../../../utils/files";

/**
 * Downloads a vault file to disk by id, reporting the outcome in a snackbar.
 *
 * The name/type and the bytes are two separate vault reads: the metadata read is
 * cache-first, and the content read is generated `void` (the endpoint answers a
 * redirect), so the bytes come off the raw response — the same escape hatch the
 * payStatement PDF download uses.
 *
 * Returns whether the file reached the disk, so a caller only fires its
 * "downloaded" analytics event when one actually did.
 */
export const useDownloadVaultFile = () => {
  const { openSnackbar } = useWSSnackbar();

  return async (fileId: string): Promise<boolean> => {
    try {
      const vaultFile = await queriesVaultFiles.getVaultFile.fetchQueryData({
        fileId
      });
      const response = await vaultFilesApi.getVaultFileContentRaw({ fileId });

      downloadFileFromBuffer(
        await response.raw.arrayBuffer(),
        // v1 always returned a filename; on v3 both it and the title are
        // optional, so the download needs a last-resort name.
        vaultFile.filename ?? vaultFile.title ?? "download",
        vaultFile.mimeType
      );
    } catch {
      openSnackbar({
        message: "Download failed",
        type: "warning"
      });

      return false;
    }

    openSnackbar({
      message: "File downloaded successfully",
      type: "success"
    });

    return true;
  };
};
